import React, { useEffect, useRef, createElement } from 'react';
import { createPortal } from 'react-dom';

export type MarqueeItem = {
  id: string;
  type: string;
};

type UseMarqueeSelectionOptions = {
  containerRef: React.RefObject<HTMLElement | null>;
  rootRef?: React.RefObject<HTMLElement | null>;
  enabled?: boolean;
  onSelect: (items: MarqueeItem[]) => void;
};

const MOVE_THRESHOLD = 4;
const SCROLL_EDGE = 56;
const SCROLL_SPEED = 20;

const IGNORE_SELECTOR = [
  'a',
  'button',
  'input',
  'textarea',
  'label',
  '.ant-checkbox-wrapper',
  '.ant-dropdown-trigger',
  '.ant-btn',
  '.b-ignore-marquee',
  '[data-no-marquee]',
  '[data-marquee-id]',
  '.draggable-item',
  '[draggable="true"]'
].join(',');

function intersects(
  a: { left: number; top: number; right: number; bottom: number },
  b: DOMRect
) {
  return !(
    a.right < b.left ||
    a.left > b.right ||
    a.bottom < b.top ||
    a.top > b.bottom
  );
}

export default function useMarqueeSelection({
  containerRef,
  rootRef,
  enabled = true,
  onSelect
}: UseMarqueeSelectionOptions) {
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const boxRef = useRef<HTMLDivElement | null>(null);
  const stateRef = useRef({
    active: false,
    moved: false,
    startX: 0,
    startY: 0,
    x: 0,
    y: 0
  });
  const rafRef = useRef(0);

  useEffect(() => {
    const container = containerRef.current;
    const root =
      rootRef?.current ??
      document.querySelector<HTMLElement>('[data-marquee-root]');
    if (!container || !root || !enabled) return;

    const clearPreview = () => {
      container
        .querySelectorAll<HTMLElement>('.marquee-preview')
        .forEach((node) => node.classList.remove('marquee-preview'));
    };

    const getItemsInRect = (
      left: number,
      top: number,
      right: number,
      bottom: number
    ) => {
      const selection = { left, top, right, bottom };
      const items: MarqueeItem[] = [];
      const seen = new Set<string>();

      container
        .querySelectorAll<HTMLElement>('[data-marquee-id]')
        .forEach((node) => {
          if (!intersects(selection, node.getBoundingClientRect())) return;
          const id = node.dataset.marqueeId;
          const type = node.dataset.marqueeType || 'item';
          if (!id || seen.has(`${type}:${id}`)) return;
          seen.add(`${type}:${id}`);
          items.push({ id, type });
        });

      return items;
    };

    const paintBox = (
      left: number,
      top: number,
      right: number,
      bottom: number
    ) => {
      const el = boxRef.current;
      if (!el) return;
      el.style.left = `${left}px`;
      el.style.top = `${top}px`;
      el.style.width = `${right - left}px`;
      el.style.height = `${bottom - top}px`;
      el.style.display = 'block';
    };

    const hideBox = () => {
      const el = boxRef.current;
      if (!el) return;
      el.style.display = 'none';
      el.style.width = '0px';
      el.style.height = '0px';
    };

    const paint = () => {
      const state = stateRef.current;
      const left = Math.min(state.startX, state.x);
      const top = Math.min(state.startY, state.y);
      const right = Math.max(state.startX, state.x);
      const bottom = Math.max(state.startY, state.y);

      paintBox(left, top, right, bottom);

      clearPreview();
      getItemsInRect(left, top, right, bottom).forEach(({ id, type }) => {
        container
          .querySelector<HTMLElement>(
            `[data-marquee-id="${id}"][data-marquee-type="${type}"]`
          )
          ?.classList.add('marquee-preview');
      });
    };

    let lastScrollTop = container.scrollTop;
    let lastScrollLeft = container.scrollLeft;

    const compensateScroll = () => {
      const state = stateRef.current;
      const dy = container.scrollTop - lastScrollTop;
      const dx = container.scrollLeft - lastScrollLeft;
      lastScrollTop = container.scrollTop;
      lastScrollLeft = container.scrollLeft;

      if (!state.active || !state.moved || (!dx && !dy)) return;

      state.startX -= dx;
      state.startY -= dy;
      paint();
    };

    const autoScroll = () => {
      const state = stateRef.current;
      const rect = container.getBoundingClientRect();
      let dy = 0;

      if (state.y > rect.bottom - SCROLL_EDGE) {
        const t = Math.min(
          1,
          (state.y - (rect.bottom - SCROLL_EDGE)) / SCROLL_EDGE
        );
        dy = SCROLL_SPEED * t;
      } else if (state.y < rect.top + SCROLL_EDGE) {
        const t = Math.min(1, (rect.top + SCROLL_EDGE - state.y) / SCROLL_EDGE);
        dy = -SCROLL_SPEED * t;
      }
      if (!dy) return;

      container.scrollTop += dy;
    };

    const tick = () => {
      const state = stateRef.current;
      if (!state.active || !state.moved) return;
      autoScroll();
      paint();
      rafRef.current = requestAnimationFrame(tick);
    };

    const stop = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
      clearPreview();
      hideBox();
    };

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      if ((e.target as HTMLElement).closest(IGNORE_SELECTOR)) return;

      stateRef.current = {
        active: true,
        moved: false,
        startX: e.clientX,
        startY: e.clientY,
        x: e.clientX,
        y: e.clientY
      };
    };

    const onMouseMove = (e: MouseEvent) => {
      const state = stateRef.current;
      if (!state.active) return;

      state.x = e.clientX;
      state.y = e.clientY;

      if (!state.moved) {
        const dx = Math.abs(state.x - state.startX);
        const dy = Math.abs(state.y - state.startY);
        if (dx < MOVE_THRESHOLD && dy < MOVE_THRESHOLD) return;
        state.moved = true;
        document.body.classList.add('marquee-selecting');
        rafRef.current = requestAnimationFrame(tick);
      }

      e.preventDefault();
    };

    const onMouseUp = () => {
      const state = stateRef.current;
      if (!state.active) return;

      if (state.moved) {
        onSelectRef.current(
          getItemsInRect(
            Math.min(state.startX, state.x),
            Math.min(state.startY, state.y),
            Math.max(state.startX, state.x),
            Math.max(state.startY, state.y)
          )
        );
        const suppress = (e: MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          document.removeEventListener('click', suppress, true);
        };
        document.addEventListener('click', suppress, true);
      }

      state.active = false;
      state.moved = false;
      document.body.classList.remove('marquee-selecting');
      stop();
    };

    const onDragStart = (e: DragEvent) => {
      if (stateRef.current.moved) e.preventDefault();
    };

    root.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    root.addEventListener('dragstart', onDragStart, true);
    container.addEventListener('scroll', compensateScroll, { passive: true });

    return () => {
      stop();
      root.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      root.removeEventListener('dragstart', onDragStart, true);
      container.removeEventListener('scroll', compensateScroll);
      document.body.classList.remove('marquee-selecting');
    };
  }, [containerRef, rootRef, enabled]);

  const marqueeBox = createPortal(
    createElement('div', {
      ref: boxRef,
      className: 'marquee-selection-box',
      style: {
        position: 'fixed',
        left: 0,
        top: 0,
        width: 0,
        height: 0,
        display: 'none'
      }
    }),
    document.body
  );

  return { marqueeBox };
}
